import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  EnvironmentProviders,
  inject,
  Injectable,
  makeEnvironmentProviders,
} from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  IMPERSONATION_READ_ONLY_MODE,
  ImpersonationService,
} from '../services/impersonation.service';

const SAFE_HTTP_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const READ_ONLY_ALLOWED_URL_PATTERNS = ['/auth/refresh-token'];

export const IMPERSONATION_READ_ONLY_ERROR_CODE = 'IMPERSONATION_READ_ONLY';
export const IMPERSONATION_READ_ONLY_MESSAGE =
  'Changes are disabled while impersonating this account.';

@Injectable()
export class ImpersonationReadOnlyInterceptor implements HttpInterceptor {
  private readonly notificationService = inject(NzNotificationService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly readOnlyModeEnabled = inject(IMPERSONATION_READ_ONLY_MODE);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.shouldBlockRequest(request)) {
      return next.handle(request);
    }

    this.notificationService.warning(
      'Read-only mode enabled',
      IMPERSONATION_READ_ONLY_MESSAGE,
    );

    return throwError(
      () =>
        new HttpErrorResponse({
          status: 403,
          statusText: 'Read-only mode enabled',
          url: request.url,
          error: {
            code: IMPERSONATION_READ_ONLY_ERROR_CODE,
            message: IMPERSONATION_READ_ONLY_MESSAGE,
          },
        }),
    );
  }

  private shouldBlockRequest(request: HttpRequest<unknown>): boolean {
    if (
      !this.readOnlyModeEnabled ||
      !this.impersonationService.isImpersonating()
    ) {
      return false;
    }

    if (SAFE_HTTP_METHODS.has(request.method.toUpperCase())) {
      return false;
    }

    return !READ_ONLY_ALLOWED_URL_PATTERNS.some((path) =>
      request.url.includes(path),
    );
  }
}

export function provideImpersonationReadOnlyMode(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: IMPERSONATION_READ_ONLY_MODE,
      useValue: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ImpersonationReadOnlyInterceptor,
      multi: true,
    },
  ]);
}
